import { handleError } from "@/utils/error";
import { useNavigate } from "@tanstack/react-router";
import { useDeleteClient, usePortfolioSummaryV2 } from "creso-core/hooks";
import { Client } from "creso-core/types";
import { Trash2Icon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from "web-design-system";

export function DeleteAccount(props: { client: Client }) {
  const { mutateAsync: deleteClient } = useDeleteClient(props.client.id);

  const { data: summary } = usePortfolioSummaryV2(
    props.client.investment_account as string
  );

  const hasExternalPortfolioSummary = !!summary?.find(
    (i) => i.source_type === "EXTERNAL"
  );

  const navigate = useNavigate();

  const handleDelete = async () => {
    try {
      await deleteClient();
      toast.success("Client deleted successfully");
      navigate({
        to: "/clients",
      });
    } catch (error) {
      handleError(error, "Failed to delete account");
    }
  };

  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className="hover:bg-muted w-full text-destructive flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0">
        <Trash2Icon />
        Delete account
      </DialogTrigger>
      <DialogContent>
        <span className="text-lg font-semibold">
          Are you sure you want to delete this account?
        </span>
        {hasExternalPortfolioSummary && (
          <span className="text-sm text-muted-foreground">
            The records of holdings imported from MFCentral will be lost.
          </span>
        )}
        <p className="text-sm mt-auto text-muted-foreground">
          This action cannot be undone.
        </p>
        <DialogFooter>
          <Button onClick={handleDelete} variant="outline">
            Yes, Delete
          </Button>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
